import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react'
import { NoiseData, NoiseParams, NoiseState } from '@popety-io/types'
import * as noiseService from '../services/noiseService'

interface NoiseContextValue extends NoiseState {
  fetchNoise: (params: NoiseParams) => Promise<void>
  clearNoise: () => void
}

const NoiseContext = createContext<NoiseContextValue | undefined>(undefined)

interface NoiseProviderProps {
  children: React.ReactNode
  initialData?: NoiseData | null // Add initial data from estimate
}

export const NoiseProvider: React.FC<NoiseProviderProps> = ({
  children,
  initialData,
}) => {
  const [state, setState] = useState<NoiseState>({
    data: initialData || null, // Use initial data if provided
    loading: false,
    error: null,
  })

  // Sync with initialData when it changes (e.g., after orchestrator saves data)
  useEffect(() => {
    if (initialData && !state.data) {
      setState((prev) => ({ ...prev, data: initialData }))
    }
  }, [initialData, state.data])

  const fetchNoise = useCallback(async (params: NoiseParams) => {
    setState((prev) => ({ ...prev, loading: true, error: null }))

    try {
      let data: NoiseData

      if (params.land_id) {
        // Fetch by land ID
        data = await noiseService.getNoiseStatisticsForLand(params.land_id)
      } else if (params.building_id) {
        data = await noiseService.getNoiseStatisticsForBuilding(
          params.building_id
        )
      } else if (params.geo_polygon) {
        // Fetch by geo_polygon
        data = await noiseService.getNoiseStatisticsForPolygon(
          params.geo_polygon
        )
      } else if (params.latitude && params.longitude) {
        // Fetch by coordinates
        data = await noiseService.fetchNoiseData({
          latitude: params.latitude,
          longitude: params.longitude,
        })
      } else {
        throw new Error('Either landId or coordinates must be provided')
      }

      setState({ data, loading: false, error: null })
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error:
          error instanceof Error ? error.message : 'Failed to fetch noise data',
      }))
    }
  }, [])

  const clearNoise = useCallback(() => {
    setState({ data: null, loading: false, error: null })
  }, [])

  return (
    <NoiseContext.Provider value={{ ...state, fetchNoise, clearNoise }}>
      {children}
    </NoiseContext.Provider>
  )
}

export const useNoise = () => {
  const context = useContext(NoiseContext)
  if (!context) {
    throw new Error('useNoise must be used within a NoiseProvider')
  }
  return context
}
